import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldIcon, TruckIcon, MapPinIcon, DropletsIcon, WindIcon, TractorIcon, AnchorIcon, BrickWallIcon, LayersIcon, TrainIcon, ZapIcon, LeafIcon, ChevronDownIcon, ChevronUpIcon, CameraIcon, MessageSquareIcon, ClipboardListIcon, SearchIcon, CheckCircleIcon, CheckIcon } from 'lucide-react';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function HomePage() {
  return <div className="w-full overflow-hidden" data-id="element-255" data-mp="2e97wj3tc32qt">
      <HeroSection data-id="element-256" data-mp="2e7wrz1s2dqyn" />
      <TrustBar data-id="element-257" data-mp="29ay06pvvhw3m" />
      <ServicePathSection data-id="element-258" data-mp="29bg50awlpin5" />
      <HowItWorksSection data-id="element-259" data-mp="29a518b35oaw1" />
      <QuoteFormSection data-id="element-260" data-mp="29aoaz4injsyz" />
      <ServicesSection data-id="element-261" data-mp="2aov3ivjpr5v7" />
      <IndustriesSection data-id="element-262" data-mp="2apoey9tmajjo" />
      <WhyChooseSection data-id="element-263" data-mp="2ao3gwa83gih2" />
      <ServiceAreaSection data-id="element-264" data-mp="2amsg8j9y3qu6" />
      <TestimonialsSection data-id="element-265" data-mp="2eq8dnhtu9v7x" />
      <FAQSection data-id="element-266" data-mp="2epf283jxqhjg" />
      <FinalCTASection data-id="element-267" data-mp="2eox93fskkjfr" />
    </div>;
}
function HeroSection() {
  return <section className="relative min-h-[580px] md:min-h-[650px] flex items-center justify-center bg-charcoal" data-id="element-268" data-mp="2ji85emzr9htf">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0" data-id="element-269" data-mp="1rq0p5no5w7fk">
        <img src="https://cdn.magicpatterns.com/patterns/generated-images/2541c035-5596-4834-ac0a-d8c59aef6fa3.jpg" alt="Merem Blasting technician in full protective gear running a mobile dustless blaster on a rusted steel structure" className="w-full h-full object-cover opacity-40" data-id="element-270" data-mp="1rr2d2sqa0oep" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/80 via-charcoal/60 to-charcoal/90" data-id="element-271" data-mp="25r3d65ypvrt4"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 w-full pt-16 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center" data-id="element-272" data-mp="3c88gx4orht59">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="lg:col-span-7" data-id="element-273" data-mp="2rfs1qcqgvxou">
          <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase leading-[1.1] tracking-tight mb-6" data-id="element-274" data-mp="31ezccs5the89">
            Mobile Sandblasting &amp; Dustless Blasting{' '}
            <span className="text-brand-red" data-id="element-275" data-mp="28qtse7ml7t9h">Across Michigan</span>
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-lg md:text-xl text-steel-200 font-body mb-6 max-w-2xl leading-relaxed" data-id="element-276" data-mp="23sjwfut1gomt">
            Merem Blasting brings mobile sandblasting, dustless blasting, and
            dry blasting to your site. Surface prep and removal of rust, paint,
            coatings, and buildup on vehicles, truck frames, boats, heavy
            equipment, concrete, and brick — with low-mess dustless options.
          </motion.p>

          <motion.p variants={fadeInUp} className="text-base text-white font-body font-semibold mb-10 max-w-2xl" data-id="element-277" data-mp="hnlbszrbs5t">
            Based in Fowlerville. Send photos for a faster quote.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-278" data-mp="3mntznhd9n1yh">
            <a href="#quote" className="w-full sm:w-auto inline-flex items-center justify-center font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 bg-brand-red text-white hover:bg-brand-red-dark transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-red" data-id="element-279" data-mp="j8wxyb4tqx1m">
              <CameraIcon className="w-5 h-5 mr-2" data-id="element-280" data-mp="2uy7n7clj6hl0" />
              Send Photos For A Quote
            </a>
            <a href="tel:5177778088" className="w-full sm:w-auto inline-flex items-center justify-center font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 border-2 border-white text-white bg-transparent hover:bg-white hover:text-charcoal transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white" data-id="element-281" data-mp="2z0u96wvit8aa">
              Call (517) 777-8088
            </a>
          </motion.div>

          <motion.div variants={fadeInUp} className="mt-8" data-id="element-282" data-mp="21gup2xuc0kq">
            <a href="https://www.facebook.com/meremblasting/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-steel-300 hover:text-white transition-colors text-sm font-medium" data-id="element-283" data-mp="1yulkyvaxd0e">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-284" data-mp="3k60nbmgxegei">
                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" data-id="element-285" data-mp="28qlose92jgha" />
              </svg>
              Message Us on Facebook
            </a>
          </motion.div>
        </motion.div>

        {/* Quick quote form */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.25,
        ease: 'easeOut'
      }} className="lg:col-span-5 w-full" data-id="element-286" data-mp="1byx1zfo7tm9r">
          <HeroQuoteForm data-id="element-287" data-mp="3hsyeya72ox1q" />
        </motion.div>
      </div>
    </section>;
}
function HeroQuoteForm() {
  /*
   * IMPLEMENTATION NOTE
   * -------------------
   * Prototype markup for the hero quick-quote form. When wiring this up on
   * the live site, use the EXISTING Merem quote form with photo upload.
   * Do not remove the upload field, rename input `name` attributes, change
   * the form action/routing, or strip conversion tracking attributes.
   */
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [fileCount, setFileCount] = useState(0);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setTimeout(() => setStatus('success'), 1200);
  };
  const fieldClass = 'w-full bg-steel-900 border border-steel-700 text-white placeholder-steel-500 px-4 py-2.5 text-sm font-body focus:outline-none focus:border-brand-red transition-colors';
  const labelClass = 'block font-heading font-bold uppercase tracking-wide text-[11px] text-steel-300 mb-1.5';
  return <div className="bg-charcoal/95 backdrop-blur-sm border border-steel-700 shadow-2xl" data-id="element-288" data-mp="1qn0iiyprgmee">
      <div className="border-t-4 border-brand-red" data-id="element-289" data-mp="2a8trlsaumzbn" />
      <div className="p-6" data-id="element-290" data-mp="2a93hlfvw96hc">
        {status === 'success' ? <div className="text-center py-10" data-id="element-291" data-mp="2f7wy7h6h9dlq">
            <div className="w-14 h-14 bg-brand-red/15 border-2 border-brand-red rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-292" data-mp="2f6vj91mcc6yq">
              <CheckCircleIcon className="w-7 h-7 text-brand-red" data-id="element-293" data-mp="258xywp7lqube" />
            </div>
            <h3 className="text-xl font-heading font-extrabold uppercase text-white mb-2" data-id="element-294" data-mp="1ri2np69ms14n">
              Request Received
            </h3>
            <p className="text-steel-300 text-sm leading-relaxed" data-id="element-295" data-mp="1w9ucpcd51yso">
              We will review your project and follow up with an estimate. Need
              it sooner? Call{' '}
              <a href="tel:5177778088" className="text-white font-semibold hover:text-brand-red transition-colors" data-id="element-296" data-mp="1mi6hr1hdhzmg">
                (517) 777-8088
              </a>
              .
            </p>
          </div> : <form onSubmit={handleSubmit} noValidate data-id="element-297" data-mp="1t0ay8eeda4fw">
            <h2 className="text-xl font-heading font-extrabold uppercase text-white mb-1" data-id="element-298" data-mp="1syzyqqryfiul">
              Get A Fast Quote
            </h2>
            <p className="text-steel-400 text-sm mb-5" data-id="element-299" data-mp="1nzdkqf4axykq">
              Attach photos and we will scope the job.
            </p>

            <div className="space-y-4" data-id="element-300" data-mp="1e3jy22wvcop8">
              <div data-id="element-301" data-mp="1e2iok2vi4a9m">
                <label htmlFor="hero-name" className={labelClass} data-id="element-302" data-mp="1e5ejrjfyskud">
                  Name <span className="text-brand-red" data-id="element-303" data-mp="1a2zzcuwndd0z">*</span>
                </label>
                <input id="hero-name" name="name" type="text" required className={fieldClass} placeholder="Your name" data-id="element-304" data-mp="159p1hj9tc6l7" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-305" data-mp="qd7tn20u1a3e">
                <div data-id="element-306" data-mp="qfk6j0rqgu9l">
                  <label htmlFor="hero-phone" className={labelClass} data-id="element-307" data-mp="mbuoyv9h7cy2">
                    Phone <span className="text-brand-red" data-id="element-308" data-mp="mdyxjqibl04o">*</span>
                  </label>
                  <input id="hero-phone" name="phone" type="tel" required className={fieldClass} placeholder="(555) 555-5555" data-id="element-309" data-mp="2xyy4vzajztm6" />
                </div>
                <div data-id="element-310" data-mp="3bygzdpafz6g0">
                  <label htmlFor="hero-location" className={labelClass} data-id="element-311" data-mp="3bwwfctlr75we">
                    Location <span className="text-brand-red" data-id="element-312" data-mp="2ectcbuzvm0h5">*</span>
                  </label>
                  <input id="hero-location" name="location" type="text" required className={fieldClass} placeholder="City" data-id="element-313" data-mp="2jb3bshsmvn8k" />
                </div>
              </div>

              <div data-id="element-314" data-mp="1vk7nvvratc6l">
                <label htmlFor="hero-service" className={labelClass} data-id="element-315" data-mp="1vimtq78wbv99">
                  What Needs Blasted?
                </label>
                <select id="hero-service" name="service" className={fieldClass} defaultValue="" data-id="element-316" data-mp="2bqwein3tk9cb">
                  <option value="" disabled data-id="element-317" data-mp="2lou2d9hro44e">
                    Select a service
                  </option>
                  <option data-id="element-318" data-mp="2qnn4y6vimaps">Automotive Sandblasting</option>
                  <option data-id="element-319" data-mp="2qn5ddnjssko7">Truck Frame Blasting</option>
                  <option data-id="element-320" data-mp="1t0ofoh75k51x">Mobile Sandblasting</option>
                  <option data-id="element-321" data-mp="1sy20pwhxmn9o">Boat Bottom Paint Removal</option>
                  <option data-id="element-322" data-mp="1y0atosut1pbs">Heavy Equipment Blasting</option>
                  <option data-id="element-323" data-mp="22ycfvmnj24rh">Concrete &amp; Brick Surface Prep</option>
                  <option data-id="element-324" data-mp="22x1c3mu30x0d">Graffiti Removal</option>
                  <option data-id="element-325" data-mp="27pkq2kwolu5p">Media Blasting</option>
                  <option data-id="element-326" data-mp="27qdmot1xf3a4">Something Else</option>
                </select>
              </div>

              {/* Photo upload — keep this field when wiring to the live form */}
              <div data-id="element-327" data-mp="1f7gpmtypzhmc">
                <label htmlFor="hero-photos" className={labelClass} data-id="element-328" data-mp="1f6x8vgkt2z9w">
                  Project Photos
                </label>
                <label htmlFor="hero-photos" className="flex items-center border-2 border-dashed border-steel-700 hover:border-brand-red bg-steel-900 px-4 py-4 cursor-pointer transition-colors" data-id="element-329" data-mp="3ohg4iu1y7ia5">
                  <CameraIcon className="w-6 h-6 text-brand-red flex-shrink-0 mr-3" data-id="element-330" data-mp="3eigsd1hz54ie" />
                  <span className="min-w-0" data-id="element-331" data-mp="39kfdksrkzrcq">
                    <span className="block font-heading font-bold uppercase tracking-wide text-white text-xs" data-id="element-332" data-mp="39m9y45yxxhga">
                      {fileCount > 0 ? `${fileCount} photo${fileCount === 1 ? '' : 's'} attached` : 'Attach Photos'}
                    </span>
                    <span className="block text-steel-500 text-[11px]" data-id="element-333" data-mp="2vqo2vh2461ff">
                      JPG, PNG or HEIC
                    </span>
                  </span>
                  <input id="hero-photos" name="photos" type="file" accept="image/*" multiple className="sr-only" onChange={e => setFileCount(e.target.files?.length ?? 0)} data-id="element-334" data-mp="2lvw2y6l272hh" />
                </label>
              </div>
            </div>

            <button type="submit" disabled={status === 'submitting'} className="mt-5 w-full inline-flex items-center justify-center font-heading font-semibold uppercase tracking-wider text-base px-6 py-3.5 bg-brand-red text-white hover:bg-brand-red-dark disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-red" data-id="element-335" data-mp="278xbwcq46a3a">
              <CameraIcon className="w-5 h-5 mr-2" data-id="element-336" data-mp="3dw2m29ci8i0c" />
              {status === 'submitting' ? 'Sending…' : 'Send Photos For A Quote'}
            </button>

            <p className="text-steel-500 text-[11px] mt-3 text-center" data-id="element-337" data-mp="177k5l2iuugzm">
              Need more fields?{' '}
              <a href="#quote" className="text-steel-300 hover:text-white underline underline-offset-2 transition-colors" data-id="element-338" data-mp="129r24h8xs131">
                Use the full quote form
              </a>
            </p>
          </form>}
      </div>
    </div>;
}
function TrustBar() {
  const items = [{
    icon: TruckIcon,
    text: 'Mobile service across Michigan'
  }, {
    icon: DropletsIcon,
    text: 'Dustless and dry blasting options'
  }, {
    icon: CameraIcon,
    text: 'Fast photo-based quotes'
  }, {
    icon: ShieldIcon,
    text: 'Proper PPE, safety-conscious jobsites'
  }, {
    icon: LayersIcon,
    text: 'Vehicles, equipment, boats, concrete & brick'
  }];
  return <div className="bg-steel-50 border-b border-steel-200 py-6" data-id="element-339" data-mp="24npw3ycntt3y">
      <div className="max-w-7xl mx-auto px-4" data-id="element-340" data-mp="24pk8upds2ewy">
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6" data-id="element-341" data-mp="e745gsfcsgt4">
          {items.map((item, idx) => <li key={idx} className="flex items-start space-x-3" data-id="element-342" data-mp="j5yysoay5nrh" data-mpi={idx}>
              <item.icon className="w-5 h-5 text-brand-red flex-shrink-0 mt-0.5" data-id="element-343" data-mp="n6is1gseg78c" />
              <span className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm leading-snug" data-id="element-344" data-mp="s5vhm9annsem">
                {item.text}
              </span>
            </li>)}
        </ul>
      </div>
    </div>;
}
function ServicePathSection() {
  const services = [{
    img: 'https://cdn.magicpatterns.com/patterns/generated-images/72e28f46-89a6-4c76-a064-80c947a357d4.jpg',
    alt: 'Classic muscle car body shell being blasted back to bare metal during restoration',
    title: 'Automotive Sandblasting',
    desc: 'Body shells, panels, and parts taken through paint, filler, and surface rust for restoration and refinishing.'
  }, {
    img: 'https://cdn.magicpatterns.com/patterns/generated-images/937041ca-2cd2-482a-ab86-8f5241b993ea.jpg',
    alt: 'Truck frame rail stripped of rust and old undercoating back to bare steel',
    title: 'Truck Frame Blasting',
    desc: 'Frames, rails, axles, and undercarriages stripped of rust, scale, and old undercoating.',
    tag: 'Most Requested'
  }, {
    img: 'https://cdn.magicpatterns.com/patterns/generated-images/e244bd33-4979-439e-8514-27cddc7be74a.jpg',
    alt: 'Mobile blasting trailer with pressure pot and compressor set up at a customer job site',
    title: 'Mobile Sandblasting',
    desc: 'We tow the rig to your shop, yard, farm, or jobsite anywhere in Michigan. No hauling required.'
  }, {
    img: 'https://cdn.magicpatterns.com/patterns/generated-images/091eb9a4-ec0e-44ab-abce-a91464c06332.jpg',
    alt: 'Boat hull on stands with antifouling bottom paint being removed',
    title: 'Boat Bottom Paint Removal',
    desc: 'Antifouling paint, blisters, and old barrier coats off hulls at the marina, yard, or on your trailer.'
  }, {
    img: 'https://cdn.magicpatterns.com/patterns/generated-images/6d2c5377-34a3-4080-971c-3840ef9740b7.jpg',
    alt: 'Excavator boom being blasted on site to remove caked mud, rust and worn paint',
    title: 'Heavy Equipment Blasting',
    desc: 'Excavators, loaders, trailers, and farm machinery cleaned of rust, worn paint, and buildup.'
  }, {
    img: 'https://cdn.magicpatterns.com/patterns/generated-images/20994a48-5505-4862-93b3-73f7bc5fb5a4.jpg',
    alt: 'Brick and concrete wall being cleaned with low-mess dustless blasting',
    title: 'Concrete & Brick Surface Prep',
    desc: 'Coatings, grime, and efflorescence off masonry, block, and concrete with controlled surface prep.'
  }, {
    img: 'https://cdn.magicpatterns.com/patterns/generated-images/02f2a3b6-e36a-4f72-b5e7-dcedbf58336e.jpg',
    alt: 'Graffiti being blasted off a concrete block wall, half stripped back to bare block',
    title: 'Graffiti Removal',
    desc: 'Tagging taken off block, brick, and concrete with media and pressure matched to the substrate.'
  }, {
    img: 'https://cdn.magicpatterns.com/patterns/generated-images/7a3e2b92-ded3-4b46-925a-912efaae6174.jpg',
    alt: 'Close-up of a metal machine part being media blasted back to bare steel on a workbench',
    title: 'Media Blasting',
    desc: 'Media matched to the job — soda, glass, garnet, and more — for parts, panels, and delicate substrates.'
  }];
  return <section className="py-20 bg-white" id="services-path" data-id="element-345" data-mp="3vmk01ax3lyzn">
      <div className="max-w-7xl mx-auto px-4" data-id="element-346" data-mp="dg5qrp9ryd7l">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="max-w-3xl mb-12" data-id="element-347" data-mp="df4bf8lq74k2">
          <p className="text-brand-red font-heading font-bold uppercase tracking-[0.2em] text-sm mb-4" data-id="element-348" data-mp="2aaaby9ay490a">
            Find Your Service
          </p>
          <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-349" data-mp="1oe8ndspygw3y">
            What Do You Need Blasted?
          </h2>
          <p className="text-lg text-steel-600 font-body" data-id="element-350" data-mp="1y760tkg3buvp">
            Pick the closest match and send photos. We handle surface prep on
            everything from a single truck frame to a full jobsite.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-351" data-mp="15jjysgmigyay">
          {services.map((svc, idx) => <motion.a key={idx} href="#quote" variants={fadeInUp} className="group flex flex-col overflow-hidden bg-charcoal border border-steel-200 hover:border-brand-red transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2" data-id="element-352" data-mp="2qvbke3verdty" data-mpi={idx}>
              <div className="relative h-44 overflow-hidden flex-shrink-0" data-id="element-353" data-mp="f9sx2jx34612">
                <img src={svc.img} alt={svc.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-354" data-mp="aevqrkr6warv" />
                <div className="absolute inset-0 bg-charcoal/35 group-hover:bg-charcoal/20 transition-colors duration-300" aria-hidden="true" data-id="element-355" data-mp="3sltm8zb7573r" />
                {svc.tag && <span className="absolute top-3 left-3 bg-brand-red text-white font-heading font-bold uppercase tracking-wider text-[11px] px-2.5 py-1" data-id="element-356" data-mp="3ipgtq4re0zal">
                    {svc.tag}
                  </span>}
              </div>
              <div className="flex flex-col flex-grow p-5" data-id="element-357" data-mp="1guzgc64x99i2">
                <h3 className="text-lg font-heading font-bold uppercase text-white leading-tight mb-2" data-id="element-358" data-mp="28pk2fzt1vj23">
                  {svc.title}
                </h3>
                <p className="text-steel-400 text-sm leading-relaxed mb-4 flex-grow" data-id="element-359" data-mp="1z0se01n77gvx">
                  {svc.desc}
                </p>
                <span className="inline-flex items-center text-brand-red font-heading font-bold text-xs uppercase tracking-wider group-hover:text-white transition-colors" data-id="element-360" data-mp="1u17cbjgn8ufi">
                  <CameraIcon className="w-4 h-4 mr-2" data-id="element-361" data-mp="1l0raeefjtvcr" />
                  Send Photos
                </span>
              </div>
            </motion.a>)}
        </motion.div>
      </div>
    </section>;
}
function HowItWorksSection() {
  const steps = [{
    icon: CameraIcon,
    title: 'Send Project Photos',
    desc: 'A few wide shots plus close-ups of the worst areas. Photos tell us more than a description ever will.'
  }, {
    icon: ClipboardListIcon,
    title: 'Add the Project Details',
    desc: 'Location, approximate size, material and coating to remove, access details, and your timing.'
  }, {
    icon: SearchIcon,
    title: 'Merem Reviews It',
    desc: 'We look over what you sent, follow up with any questions, and send back a written estimate.'
  }, {
    icon: TruckIcon,
    title: 'We Come to the Site',
    desc: 'Once approved, we schedule a date and tow the mobile blasting rig to your location.'
  }, {
    icon: CheckCircleIcon,
    title: 'Surface Is Prepped',
    desc: 'You get a cleaned, prepped surface ready for primer, paint, coating, or reassembly.'
  }];
  return <section className="py-20 bg-steel-50 border-y border-steel-200" data-id="element-362" data-mp="38z1r9d4pjo21">
      <div className="max-w-7xl mx-auto px-4" data-id="element-363" data-mp="38wyu89550unt">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-3xl mb-14" data-id="element-364" data-mp="ja9dlxemas9t">
          <p className="text-brand-red font-heading font-bold uppercase tracking-[0.2em] text-sm mb-4" data-id="element-365" data-mp="3up1n3tp43jtm">
            The Process
          </p>
          <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-366" data-mp="3ptci3aubpapx">
            How It Works
          </h2>
          <p className="text-lg text-steel-600 font-body" data-id="element-367" data-mp="3jibsrr0pmkn">
            Five steps from your first photo to a prepped surface. Most
            projects get scoped without a site visit.
          </p>
        </motion.div>

        <motion.ol initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-368" data-mp="371tdizknhols">
          {steps.map((step, idx) => <motion.li key={idx} variants={fadeInUp} className="relative bg-white border border-steel-200 p-6 hover:border-brand-red transition-colors duration-300" data-id="element-369" data-mp="2t7sncu7rl9x1" data-mpi={idx}>
              <span className="absolute top-0 right-0 font-heading font-extrabold text-5xl leading-none text-steel-100 pr-3 pt-1 select-none" data-id="element-370" data-mp="13pvh80gn2p9c">
                {idx + 1}
              </span>
              <step.icon className="w-8 h-8 text-brand-red mb-5 relative" data-id="element-371" data-mp="tvlqen7chz0q" />
              <h3 className="font-heading font-bold uppercase tracking-wide text-charcoal mb-2 relative" data-id="element-372" data-mp="ytn8b4t1bsam">
                {step.title}
              </h3>
              <p className="text-steel-600 text-sm leading-relaxed relative" data-id="element-373" data-mp="1rhj0bw7nsw12">
                {step.desc}
              </p>
            </motion.li>)}
        </motion.ol>
      </div>
    </section>;
}
function QuoteFormSection() {
  /*
   * IMPLEMENTATION NOTE
   * -------------------
   * This is the prototype markup for the Get A Quote section.
   * When wiring this up on the live site, use the EXISTING Merem quote form
   * with photo upload. Do not remove the upload field, rename any input
   * `name` attributes, change the form action/routing, or strip tracking
   * attributes — conversion tracking for Google Ads depends on them.
   * Field names below intentionally mirror the standard quote payload.
   */
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [fileCount, setFileCount] = useState(0);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setTimeout(() => setStatus('success'), 1200);
  };
  const fieldClass = 'w-full bg-steel-900 border border-steel-700 text-white placeholder-steel-500 px-4 py-3 font-body focus:outline-none focus:border-brand-red transition-colors';
  const labelClass = 'block font-heading font-bold uppercase tracking-wide text-xs text-steel-300 mb-2';
  return <section className="py-20 bg-charcoal text-white" id="quote" data-id="element-374" data-mp="3g2rmj46nu2hk">
      <div className="max-w-7xl mx-auto px-4" data-id="element-375" data-mp="3fzx23cnncho5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-376" data-mp="3bzlebqzzm18g">
          {/* Left: pitch + alternate contact paths */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="lg:col-span-5" data-id="element-377" data-mp="1rgj1it4ggnj4">
            <p className="text-brand-red font-heading font-bold uppercase tracking-[0.2em] text-sm mb-4" data-id="element-378" data-mp="259qv2qc3jt3g">
              Get A Quote
            </p>
            <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase mb-6 leading-tight" data-id="element-379" data-mp="2a8lm2fk7wnyn">
              Send Photos,{' '}
              <span className="text-brand-red" data-id="element-380" data-mp="2j9s4wvacaav2">Get a Faster Quote</span>
            </h2>
            <p className="text-lg text-steel-300 font-body mb-8 leading-relaxed" data-id="element-381" data-mp="2j7yy9fzq7qn2">
              Photos are the fastest way to get a real number. Attach a few
              shots of the project, fill in the details, and we will get back
              to you with a written estimate.
            </p>

            <ul className="space-y-3 mb-8" data-id="element-382" data-mp="twop0p7milxj">
              {['Project photos (wide shots and close-ups)', 'Location of the work', 'Approximate size or part count', 'Material and coating to remove', 'Access details — parking, power, water, indoor or outdoor', 'Timing and any deadline'].map(item => <li key={item} className="flex items-start" data-id="element-383" data-mp="2g4dhms9f4sup">
                  <CheckIcon className="w-4 h-4 text-brand-red mt-1 mr-3 flex-shrink-0" data-id="element-384" data-mp="2g2snv4uxhdxw" />
                  <span className="text-steel-300 text-sm leading-relaxed" data-id="element-385" data-mp="2b50ue6emxjrl">
                    {item}
                  </span>
                </li>)}
            </ul>

            <div className="border-t border-steel-800 pt-6 space-y-3" data-id="element-386" data-mp="2kweqe47do1fz">
              <p className="font-heading font-bold uppercase tracking-wide text-sm text-white" data-id="element-387" data-mp="2yulfovy9gt41">
                Prefer to send them another way?
              </p>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-388" data-mp="2ttxfpks211xo">
                <a href="sms:5177778088" className="inline-flex items-center justify-center font-heading font-semibold uppercase tracking-wider text-sm px-5 py-3 border-2 border-white text-white bg-transparent hover:bg-white hover:text-charcoal transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white" data-id="element-389" data-mp="3dj4zqazvgwdz">
                  <MessageSquareIcon className="w-4 h-4 mr-2" data-id="element-390" data-mp="2uq11sdfmfxdk" />
                  Text (517) 777-8088
                </a>
                <a href="tel:5177778088" className="inline-flex items-center justify-center font-heading font-semibold uppercase tracking-wider text-sm px-5 py-3 border-2 border-steel-700 text-steel-300 bg-transparent hover:border-white hover:text-white transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white" data-id="element-391" data-mp="2zjvlhgv88ccn">
                  Call Instead
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right: the quote form */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="lg:col-span-7" data-id="element-392" data-mp="2rb971cvc11yr">
            <div className="bg-steel-950 border border-steel-800 p-6 md:p-8" data-id="element-393" data-mp="2dagc4r14c4v4">
              {status === 'success' ? <div className="text-center py-16" data-id="element-394" data-mp="28jq6rdsp020f">
                  <div className="w-16 h-16 bg-brand-red/15 border-2 border-brand-red rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-395" data-mp="28hwzpxe63frw">
                    <CheckCircleIcon className="w-8 h-8 text-brand-red" data-id="element-396" data-mp="1ymljtbr0w2ik" />
                  </div>
                  <h3 className="text-2xl font-heading font-extrabold uppercase text-white mb-3" data-id="element-397" data-mp="1tmz50xvjqe7n">
                    Request Received
                  </h3>
                  <p className="text-steel-300 max-w-md mx-auto leading-relaxed" data-id="element-398" data-mp="1pnhzk4g4dh2h">
                    Thanks — we have your photos and details. We will review
                    the project and follow up with an estimate. Need it sooner?
                    Call{' '}
                    <a href="tel:5177778088" className="text-white font-semibold hover:text-brand-red transition-colors" data-id="element-399" data-mp="e4b1fldcqghx">
                      (517) 777-8088
                    </a>
                    .
                  </p>
                </div> : <form onSubmit={handleSubmit} noValidate data-id="element-400" data-mp="3mh8gi1wmml5s">
                  {/* Photo upload — keep this field when wiring to the live form */}
                  <div className="mb-6" data-id="element-401" data-mp="51vrfmrv4s7">
                    <label htmlFor="photos" className={labelClass} data-id="element-402" data-mp="639jrv6a5dm">
                      Project Photos <span className="text-brand-red" data-id="element-403" data-mp="2ytqmdzpw3azn">*</span>
                    </label>
                    <label htmlFor="photos" className="flex flex-col items-center justify-center border-2 border-dashed border-steel-700 hover:border-brand-red bg-steel-900 px-6 py-10 cursor-pointer transition-colors text-center" data-id="element-404" data-mp="33rip7os2ohim">
                      <CameraIcon className="w-9 h-9 text-brand-red mb-3" data-id="element-405" data-mp="24hq48n7216z2" />
                      <span className="font-heading font-bold uppercase tracking-wide text-white text-sm mb-1" data-id="element-406" data-mp="28jblekqmg7f2">
                        {fileCount > 0 ? `${fileCount} photo${fileCount === 1 ? '' : 's'} attached` : 'Attach Project Photos'}
                      </span>
                      <span className="text-steel-500 text-xs" data-id="element-407" data-mp="1kvtn06cuqb34">
                        JPG, PNG or HEIC — add as many as you need
                      </span>
                      <input id="photos" name="photos" type="file" accept="image/*" multiple className="sr-only" onChange={e => setFileCount(e.target.files?.length ?? 0)} data-id="element-408" data-mp="1bu2i33q4y8ab" />
                    </label>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-409" data-mp="165pbjak834zj">
                    <div data-id="element-410" data-mp="124n9yn2u2gue">
                      <label htmlFor="name" className={labelClass} data-id="element-411" data-mp="128kvtggspmhz">
                        Name <span className="text-brand-red" data-id="element-412" data-mp="1g4evr9zld1iy">*</span>
                      </label>
                      <input id="name" name="name" type="text" required className={fieldClass} placeholder="Your name" data-id="element-413" data-mp="1b6dcany9706y" />
                    </div>
                    <div data-id="element-414" data-mp="1ywqvrq2rvqpx">
                      <label htmlFor="phone" className={labelClass} data-id="element-415" data-mp="1txe9b6ftclnv">
                        Phone <span className="text-brand-red" data-id="element-416" data-mp="1tz7eehas2xtr">*</span>
                      </label>
                      <input id="phone" name="phone" type="tel" required className={fieldClass} placeholder="(555) 555-5555" data-id="element-417" data-mp="rlt5a421d9m2" />
                    </div>
                    <div data-id="element-418" data-mp="15d7p2d3rpeu6">
                      <label htmlFor="email" className={labelClass} data-id="element-419" data-mp="1aazf11kuqqvq">
                        Email
                      </label>
                      <input id="email" name="email" type="email" className={fieldClass} placeholder="you@example.com" data-id="element-420" data-mp="1f89d15mb4av6" />
                    </div>
                    <div data-id="element-421" data-mp="3m0s75iiq7nw0">
                      <label htmlFor="service" className={labelClass} data-id="element-422" data-mp="3m1la08ekdt8v">
                        Service Needed
                      </label>
                      <select id="service" name="service" className={fieldClass} defaultValue="" data-id="element-423" data-mp="3ii30d8dkbl0h">
                        <option value="" disabled data-id="element-424" data-mp="38rrtpe2huxpw">
                          Select a service
                        </option>
                        <option data-id="element-425" data-mp="34rzfakqf5xcm">Automotive Sandblasting</option>
                        <option data-id="element-426" data-mp="5c2uuskt4n0n">Truck Frame Blasting</option>
                        <option data-id="element-427" data-mp="59gnasxx27if">Mobile Sandblasting</option>
                        <option data-id="element-428" data-mp="dj6ymmnl15n">Boat Bottom Paint Removal</option>
                        <option data-id="element-429" data-mp="3sg0kbjoe2vna">Heavy Equipment Blasting</option>
                        <option data-id="element-430" data-mp="3sefrpzlmxms2">Concrete &amp; Brick Surface Prep</option>
                        <option data-id="element-431" data-mp="3nge6p94nfddy">Graffiti Removal</option>
                        <option data-id="element-432" data-mp="2h13q7vdclg92">Media Blasting</option>
                        <option data-id="element-433" data-mp="2h2ompqj8399k">Something Else</option>
                      </select>
                    </div>
                    <div data-id="element-434" data-mp="2c0pkijz7yie7">
                      <label htmlFor="location" className={labelClass} data-id="element-435" data-mp="3tb0fy6v1l8q">
                        Project Location{' '}
                        <span className="text-brand-red" data-id="element-436" data-mp="8kko4t8umehz">*</span>
                      </label>
                      <input id="location" name="location" type="text" required className={fieldClass} placeholder="City or address" data-id="element-437" data-mp="3qya95h1m3mo6" />
                    </div>
                    <div data-id="element-438" data-mp="rhu7ybi35tzr">
                      <label htmlFor="size" className={labelClass} data-id="element-439" data-mp="ri3x5wvb3x4e">
                        Approximate Size
                      </label>
                      <input id="size" name="size" type="text" className={fieldClass} placeholder="Dimensions, sq ft, or part count" data-id="element-440" data-mp="ihys3tdl5ate" />
                    </div>
                    <div data-id="element-441" data-mp="2ur5nckdbh1hb">
                      <label htmlFor="material" className={labelClass} data-id="element-442" data-mp="2uqe325p66q6c">
                        Material &amp; Coating to Remove
                      </label>
                      <input id="material" name="material" type="text" className={fieldClass} placeholder="Steel with rust, paint, undercoating…" data-id="element-443" data-mp="34plcihssa0o6" />
                    </div>
                    <div data-id="element-444" data-mp="272cz7a2xrxx9">
                      <label htmlFor="timing" className={labelClass} data-id="element-445" data-mp="272w8y3ifng07">
                        Timing
                      </label>
                      <input id="timing" name="timing" type="text" className={fieldClass} placeholder="ASAP, next month, flexible…" data-id="element-446" data-mp="2lstyonw0gdri" />
                    </div>
                  </div>

                  <div className="mt-5" data-id="element-447" data-mp="3odmdzers9ueh">
                    <label htmlFor="access" className={labelClass} data-id="element-448" data-mp="3octg73asyf8h">
                      Access Details
                    </label>
                    <textarea id="access" name="access" rows={3} className={fieldClass} placeholder="Where can we park the rig? Power and water on site? Indoor or outdoor? Anything nearby we should protect?" data-id="element-449" data-mp="3tal5dpk2bn8z" />
                  </div>

                  <button type="submit" disabled={status === 'submitting'} className="mt-7 w-full inline-flex items-center justify-center font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 bg-brand-red text-white hover:bg-brand-red-dark disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-red" data-id="element-450" data-mp="3aev64a3il4ys">
                    <CameraIcon className="w-5 h-5 mr-2" data-id="element-451" data-mp="18rppxezw5iy0" />
                    {status === 'submitting' ? 'Sending…' : 'Send Photos For A Quote'}
                  </button>

                  <p className="text-steel-500 text-xs mt-4 text-center" data-id="element-452" data-mp="z0l909qnno0j">
                    We use your details only to quote and schedule your
                    project.
                  </p>
                </form>}
            </div>

            {/* Prototype hand-off note for the developer wiring this up */}
            <div className="mt-4 border-l-4 border-brand-red bg-steel-900 px-4 py-3" data-id="element-453" data-mp="g3k1ah5pv9sw">
              <p className="text-steel-400 text-xs leading-relaxed" data-id="element-454" data-mp="g5dbg6fjgc5n">
                <span className="font-heading font-bold uppercase tracking-wide text-white" data-id="element-455" data-mp="c3ji6j7oaafi">
                  Implementation note:
                </span>{' '}
                Use the existing Merem quote form with photo upload here.
                Preserve the current upload field, input names, form routing,
                and conversion tracking.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function ServicesSection() {
  const services = [{
    icon: DropletsIcon,
    title: 'Dustless Blasting',
    desc: 'Low-mess wet blasting that helps suppress airborne dust while stripping coatings and rust from the surface.'
  }, {
    icon: WindIcon,
    title: 'Dry Blasting',
    desc: 'Traditional abrasive blasting for heavy-duty coating removal and surface preparation.'
  }, {
    icon: LayersIcon,
    title: 'Powder Coating Removal',
    desc: 'Strip powder coating from metal parts, frames, wheels, and equipment.'
  }, {
    icon: TractorIcon,
    title: 'Farm & Agricultural Equipment',
    desc: 'Tractors, implements, grain equipment, and trailers cleaned of rust, paint, and buildup on the farm.'
  }, {
    icon: TrainIcon,
    title: 'Rail Car Blasting',
    desc: 'Blast and prepare rail cars and rolling stock for repainting and maintenance.'
  }, {
    icon: BrickWallIcon,
    title: 'Industrial Steel & Structures',
    desc: 'Tanks, beams, railings, and structural steel prepped on site for recoating.'
  }, {
    icon: AnchorIcon,
    title: 'Marine & Trailer Prep',
    desc: 'Boat trailers, docks, and marine hardware stripped of corrosion and old coatings.'
  }];
  return <section className="py-20 bg-white" id="services" data-id="element-456" data-mp="o3n7ru5682za">
      <div className="max-w-7xl mx-auto px-4" data-id="element-457" data-mp="o2vpfl0l41qy">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16" data-id="element-458" data-mp="123esedq70hm9">
          <p className="text-brand-red font-heading font-bold uppercase tracking-[0.2em] text-sm mb-4" data-id="element-459" data-mp="33w2ycuu81j5q">
            Additional Services
          </p>
          <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-460" data-mp="1f2s37cirq7ww">
            Other Blasting &amp; Surface Prep Work
          </h2>
          <p className="text-lg text-steel-600 font-body" data-id="element-461" data-mp="1o25jao1eduql">
            Beyond our most-requested services, we handle these regularly. Do
            not see your project? Send photos and ask.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-462" data-mp="1xzjlpjqhkizw">
          {services.map((service, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-steel-50 p-8 border border-steel-100 hover:border-brand-red hover:shadow-lg transition-all duration-300 group" data-id="element-463" data-mp="22tntg57ksv21" data-mpi={idx}>
              <service.icon className="w-10 h-10 text-brand-red mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-464" data-mp="2vd9oglsw5dvn" />
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3" data-id="element-465" data-mp="30bjk0xij2ghs">
                {service.title}
              </h3>
              <p className="text-steel-600 mb-6 text-sm leading-relaxed" data-id="element-466" data-mp="2qeopeppdl08s">
                {service.desc}
              </p>
              <a href="#quote" className="inline-flex items-center text-brand-red font-bold text-sm uppercase tracking-wider hover:text-brand-red-dark" data-id="element-467" data-mp="2cms00tf3d4g3">
                Send Photos{' '}
                <span className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-468" data-mp="22qp0lvbmjjwu">
                  →
                </span>
              </a>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
function IndustriesSection() {
  const industries = ['Automotive', 'Construction', 'Agriculture', 'Commercial Properties', 'Industrial & Manufacturing', 'Gas & Oil', 'Aviation & Aerospace', 'Railway', 'Marine'];
  return <section className="py-16 bg-charcoal text-white overflow-hidden" data-id="element-469" data-mp="3vl6h652l5rvm">
      <div className="max-w-7xl mx-auto px-4" data-id="element-470" data-mp="3qm3l3a4l2vzs">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} data-id="element-471" data-mp="3qn50fqsmu4nb">
          <h2 className="text-2xl md:text-4xl font-heading font-extrabold uppercase mb-10 text-center" data-id="element-472" data-mp="n4v2up5y1u4j">
            Industries We Serve
          </h2>

          {/* Mobile horizontal scroll, Desktop grid */}
          <div className="flex overflow-x-auto pb-6 md:pb-0 md:grid md:grid-cols-3 lg:grid-cols-5 gap-4 hide-scrollbar" data-id="element-473" data-mp="e3x1yfqsy2q6">
            {industries.map((ind, idx) => <div key={idx} className="flex-shrink-0 w-48 md:w-auto bg-steel-900 border border-steel-800 rounded-sm p-4 text-center hover:bg-brand-red hover:border-brand-red transition-colors cursor-default" data-id="element-474" data-mp="2yvz1hdoneczm" data-mpi={idx}>
                <span className="font-heading font-semibold tracking-wide uppercase text-sm" data-id="element-475" data-mp="19cqtipnn0ung">
                  {ind}
                </span>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}
function WhyChooseSection() {
  const features = [{
    icon: ZapIcon,
    title: 'Quick Estimates & Scheduling',
    desc: 'Send photos and project details and we will get back to you with a clear estimate and a realistic date.'
  }, {
    icon: ShieldIcon,
    title: 'OSHA-Conscious & Safety-Forward',
    desc: 'Full PPE on every job, with setup and containment planned around your site and the people working near it.'
  }, {
    icon: LeafIcon,
    title: 'EPA & DEQ-Conscious Methods',
    desc: 'Low-mess dustless options can reduce airborne dust and containment needs compared with open dry blasting.'
  }, {
    icon: TruckIcon,
    title: 'Fully Mobile, Statewide',
    desc: 'We come to you anywhere in Michigan. No need to transport your equipment or project.'
  }];
  return <section className="py-20 bg-white" data-id="element-476" data-mp="38miayz5ev91c">
      <div className="max-w-7xl mx-auto px-4" data-id="element-477" data-mp="3mkqfpt8oven2">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-478" data-mp="3mjfb5r7f62uw">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-479" data-mp="3hmf69jmb365d">
            <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-10" data-id="element-480" data-mp="2oy17v2rb27ta">
              Why Michigan Trusts{' '}
              <span className="text-brand-red" data-id="element-481" data-mp="2yumaxijrl8uh">Merem Blasting</span>
            </h2>

            <div className="space-y-8" data-id="element-482" data-mp="2yst33zxf0ikw">
              {features.map((feat, idx) => <motion.div key={idx} variants={fadeInUp} className="flex" data-id="element-483" data-mp="2b2yrtmsqv22r" data-mpi={idx}>
                  <div className="flex-shrink-0 mt-1" data-id="element-484" data-mp="265ffisflwzdg">
                    <div className="w-12 h-12 bg-steel-50 rounded-full flex items-center justify-center border border-steel-200" data-id="element-485" data-mp="lo6690ze5prw">
                      <feat.icon className="w-6 h-6 text-brand-red" data-id="element-486" data-mp="qfejy97r7xav" />
                    </div>
                  </div>
                  <div className="ml-6" data-id="element-487" data-mp="cp2pw4rlzoh3">
                    <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-2" data-id="element-488" data-mp="gojw50ev0pnb">
                      {feat.title}
                    </h3>
                    <p className="text-steel-600 leading-relaxed" data-id="element-489" data-mp="14f75zr8c5pci">
                      {feat.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="order-first lg:order-last h-64 md:h-96 lg:h-[600px] relative rounded-sm overflow-hidden shadow-2xl" data-id="element-490" data-mp="10cslz3sxkjjn">
            <img src="https://cdn.magicpatterns.com/patterns/generated-images/0751e7ca-4cd4-4589-9f17-de2eb5d448b6.jpg" alt="Blasting technician in a supplied-air hood, heavy coveralls, and gloves holding a blast nozzle on a job site" className="w-full h-full object-cover" data-id="element-491" data-mp="1lpt8sgiagm9p" />
            <div className="absolute inset-0 bg-brand-red/10 mix-blend-multiply" data-id="element-492" data-mp="12xiakzykxchx"></div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function MichiganServiceMap() {
  return <div className="w-full border border-steel-800 bg-steel-900 shadow-2xl" data-id="element-493" data-mp="twk6kho554zc">
      {/* Frame header */}
      <div className="flex items-center justify-between border-b border-steel-800 px-5 py-3.5" data-id="element-494" data-mp="2sbwsp1f3e14g">
        <div className="flex items-center" data-id="element-495" data-mp="2oautgkl6dp2h">
          <span className="w-2.5 h-2.5 rounded-full bg-brand-red mr-3 flex-shrink-0" aria-hidden="true" data-id="element-496" data-mp="2obwdvfo2znwv" />
          <span className="font-heading font-bold uppercase tracking-wider text-sm text-white" data-id="element-497" data-mp="a0zj59nbf5aq">
            Fowlerville, MI — Home Base
          </span>
        </div>
        <span className="hidden sm:inline font-heading font-semibold uppercase tracking-widest text-xs text-steel-400" data-id="element-498" data-mp="jt5campazsri">
          Statewide Coverage
        </span>
      </div>

      {/* Live map */}
      <iframe title="Map of Michigan showing Merem Blasting's Fowlerville home base and statewide service area" src="https://maps.google.com/maps?q=Fowlerville,+MI&z=7&output=embed" className="w-full h-[380px] md:h-[520px] block border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen data-id="element-499" data-mp="3nawzzowepqjw" />

      {/* Frame footer */}
      <div className="border-t border-steel-800 px-5 py-3.5" data-id="element-500" data-mp="1bj9se9615tmx">
        <p className="text-steel-400 text-xs leading-relaxed" data-id="element-501" data-mp="1gicwnre6rvtu">
          We travel from Fowlerville to job sites across the Lower Peninsula
          and beyond.{' '}
          <a href="https://www.google.com/maps/dir/?api=1&destination=Fowlerville,+MI" target="_blank" rel="noopener noreferrer" className="text-white font-semibold hover:text-brand-red transition-colors underline underline-offset-2" data-id="element-502" data-mp="s3ychutl24p6">
            Get directions
          </a>
        </p>
      </div>
    </div>;
}
function ServiceAreaSection() {
  const cities = ['Fowlerville (HQ)', 'Lansing / East Lansing', 'Grand Rapids', 'Detroit Metro', 'Ann Arbor', 'Flint', 'Kalamazoo', 'Traverse City', 'Saginaw / Bay City'];
  return <section className="py-20 bg-charcoal text-white relative overflow-hidden" data-id="element-503" data-mp="1fvdibxkkj456">
      <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-504" data-mp="3lov7iur3gwb7">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center" data-id="element-505" data-mp="3lp52kwqzxtnr">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="lg:col-span-5" data-id="element-506" data-mp="3houk4km0pas0">
            <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase mb-6" data-id="element-507" data-mp="3bmraegr75tli">
              Serving All of <span className="text-brand-red" data-id="element-508" data-mp="32smp0cuu1add">Michigan</span>
            </h2>
            <p className="text-lg text-steel-300 font-body mb-10 max-w-lg leading-relaxed" data-id="element-509" data-mp="32qi9syrhgoxs">
              Based in Fowlerville. Mobile blasting across Michigan. Send
              project photos, location, and what needs removed for a faster
              quote.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-10" data-id="element-510" data-mp="2ew8lha11oer0">
              {cities.map((city, idx) => <div key={idx} className="flex items-center space-x-3" data-id="element-511" data-mp="2jvlgwj5zeu57" data-mpi={idx}>
                  <MapPinIcon className="w-5 h-5 text-brand-red flex-shrink-0" data-id="element-512" data-mp="d42is9i3iroy" />
                  <span className="font-heading font-semibold tracking-wide text-lg" data-id="element-513" data-mp="i12qspyia4ip">
                    {city}
                  </span>
                </div>)}
            </div>

            <div className="bg-steel-900 border-l-4 border-brand-red p-6" data-id="element-514" data-mp="1ou68v63vrs4e">
              <p className="font-bold text-white mb-2" data-id="element-515" data-mp="1tro7o3o6oqyo">Not listed?</p>
              <p className="text-steel-300 text-sm mb-4" data-id="element-516" data-mp="1tqd3w3uqnj7k">
                We travel statewide and likely cover your area. Send photos and
                we will confirm.
              </p>
              <a href="sms:5177778088" className="inline-flex items-center justify-center font-heading font-semibold uppercase tracking-wider text-sm px-5 py-3 bg-brand-red text-white hover:bg-brand-red-dark transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-red" data-id="element-517" data-mp="22mm43kqjilur">
                <CameraIcon className="w-4 h-4 mr-2" data-id="element-518" data-mp="i696m8n3q4lt" />
                Send Photos For A Quote
              </a>
            </div>
          </motion.div>

          {/* Michigan service area map */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="lg:col-span-7 w-full" data-id="element-519" data-mp="30flrgrf8wbpl">
            <MichiganServiceMap data-id="element-520" data-mp="2mmmgn2qer5od" />
          </motion.div>
        </div>
      </div>
    </section>;
}
function TestimonialsSection() {
  const testimonials = [{
    quote: 'They came out to our yard and prepped three pieces of machinery in far less time than our crew would have taken. Setup and cleanup were handled well.',
    name: 'Industrial Facility Manager',
    project: 'Heavy Equipment Blasting',
    loc: 'Grand Rapids, MI'
  }, {
    quote: 'We needed years of coating and grime off the brick on our building. They walked the wall with us first, matched the media to it, and kept the site controlled.',
    name: 'Commercial Property Owner',
    project: 'Concrete & Brick Surface Prep',
    loc: 'Detroit, MI'
  }, {
    quote: "I sent photos of my '68 Camaro frame and had a quote back quickly. They stripped it to bare metal on schedule and it went straight into primer.",
    name: 'Auto Restoration Enthusiast',
    project: 'Classic Car Restoration Prep',
    loc: 'Lansing, MI'
  }];
  return <section className="py-20 bg-white" data-id="element-521" data-mp="1rfde62x4b4hk">
      <div className="max-w-7xl mx-auto px-4" data-id="element-522" data-mp="1coe7ixfueq0m">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center mb-16" data-id="element-523" data-mp="1cmjojoo4t7z6">
          <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-524" data-mp="17pji3cnde37j">
            What Our Clients Say
          </h2>
          <p className="text-steel-500 text-sm italic" data-id="element-525" data-mp="tr3e3tgw6qt3">
            Project highlights based on real work. Contact us for references.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-526" data-mp="f2ju2efa6nj5">
          {testimonials.map((test, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-steel-50 p-8 border border-steel-100 relative" data-id="element-527" data-mp="k1mypxrcmrql" data-mpi={idx}>
              <div className="absolute top-4 right-6 text-6xl font-heading text-steel-200 opacity-50" data-id="element-528" data-mp="xzkyny4xalmk">
                "
              </div>
              <p className="text-steel-700 italic mb-8 relative z-10 leading-relaxed" data-id="element-529" data-mp="17s0iz23ozmam">
                "{test.quote}"
              </p>
              <div data-id="element-530" data-mp="12tfozkf19yum">
                <p className="font-heading font-bold uppercase text-charcoal text-lg" data-id="element-531" data-mp="12v0bcmr72bhe">
                  {test.name}
                </p>
                <p className="text-brand-red text-sm font-semibold" data-id="element-532" data-mp="1crmu95zymsgs">
                  {test.project}
                </p>
                <p className="text-steel-500 text-xs mt-1" data-id="element-533" data-mp="368rd4xu31p09">{test.loc}</p>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
function FAQSection() {
  const faqs = [{
    q: 'What is dustless blasting?',
    a: 'Dustless blasting uses a mixture of water, air, and abrasive media to remove coatings, rust, and buildup from surfaces. Adding water helps knock down airborne dust compared with open dry blasting, which can reduce the containment required on many sites. It is a low-mess option, not a dust-free one, so we still plan protection for the surrounding area.'
  }, {
    q: "What's the difference between dustless and dry blasting?",
    a: 'Dustless (wet) blasting adds water to help suppress dust and reduce heat, which suits sensitive surfaces and work near occupied areas. Dry blasting uses abrasive media without water and is typically more aggressive on heavy rust and thick coatings. We offer both so we can match the process to your project.'
  }, {
    q: 'Do you come to my location?',
    a: 'Yes. We are a fully mobile operation based in Fowlerville, Michigan. We bring all equipment to your site anywhere in the state. No need to transport your vehicle, equipment, or project to us.'
  }, {
    q: 'How fast can you start?',
    a: 'It depends on the job and our current schedule. Sending photos with your location, approximate size, and what needs to come off is the quickest way to get an estimate back. Call (517) 777-8088 if you need to talk through timing.'
  }, {
    q: 'What surfaces can you blast?',
    a: "Metal, steel, aluminum, concrete, brick, stone, wood, fiberglass, and more. We work on vehicles, heavy equipment, buildings, boats, rail cars, and industrial components. If you're unsure, just ask."
  }, {
    q: 'Will blasting harm my vehicle or equipment?',
    a: 'We adjust media, pressure, and technique to the substrate, and wet blasting adds water that cushions the impact and reduces heat buildup on thin panels. Every piece is different, so send photos of what you have and we will tell you the approach we would take and any areas we would recommend handling differently.'
  }];
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return <section className="py-20 bg-steel-50" data-id="element-534" data-mp="3dtb9ysp4ywmc">
      <div className="max-w-4xl mx-auto px-4" data-id="element-535" data-mp="5cq77tn3kzro">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center mb-12" data-id="element-536" data-mp="5e17vkl8xrek">
          <h2 className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal" data-id="element-537" data-mp="3rmu33z7jct24">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <div className="space-y-4" data-id="element-538" data-mp="2g3q30ak64v2j">
          {faqs.map((faq, idx) => <motion.div key={idx} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white border border-steel-200 rounded-sm overflow-hidden" data-id="element-539" data-mp="2l5faxhc3gsme" data-mpi={idx}>
              <button className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red" onClick={() => setOpenIdx(openIdx === idx ? null : idx)} aria-expanded={openIdx === idx} data-id="element-540" data-mp="2ml1b9a5ucm5e">
                <span className="font-heading font-bold text-lg uppercase text-charcoal pr-8" data-id="element-541" data-mp="1pt7hs1ef5viq">
                  {faq.q}
                </span>
                {openIdx === idx ? <ChevronUpIcon className="w-5 h-5 text-brand-red flex-shrink-0" data-id="element-542" data-mp="1yunr05thzrld" /> : <ChevronDownIcon className="w-5 h-5 text-steel-400 flex-shrink-0" data-id="element-543" data-mp="1b4l4e58crdu5" />}
              </button>
              <AnimatePresence data-id="element-544" data-mp="llhhm1bv101l">
                {openIdx === idx && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-545" data-mp="lnanvfik9i92">
                    <div className="px-6 pb-6 text-steel-600 leading-relaxed" data-id="element-546" data-mp="18jujmcqmannr">
                      {faq.a}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
function FinalCTASection() {
  return <section className="py-24 bg-brand-red text-white text-center px-4" data-id="element-547" data-mp="1s9emz7nxr3oe">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-548" data-mp="1s7jyjjdggtfk">
        <h2 className="text-4xl md:text-6xl font-heading font-extrabold uppercase mb-6" data-id="element-549" data-mp="37v8f9yauk19b">
          Got a Project? Send Photos.
        </h2>
        <p className="text-xl font-body mb-10 text-white/90" data-id="element-550" data-mp="2xvhj7s6n1e74">
          A few pictures, your location, and what needs to come off is all we
          need to start scoping the job.
        </p>
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8" data-id="element-551" data-mp="3qj3nz3rlqovk">
          <a href="sms:5177778088" className="w-full sm:w-auto inline-flex items-center justify-center font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 bg-white text-brand-red hover:bg-steel-100 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white" data-id="element-552" data-mp="3ls54jvbbhao3">
            <CameraIcon className="w-5 h-5 mr-2" data-id="element-553" data-mp="23xo1a9cuxc7f" />
            Send Photos For A Quote
          </a>
          <a href="tel:5177778088" className="w-full sm:w-auto inline-flex items-center justify-center font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 border-2 border-white text-white bg-transparent hover:bg-white hover:text-brand-red transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white" data-id="element-554" data-mp="1yykv2ll54zxv">
            Call (517) 777-8088
          </a>
        </div>
        <p className="text-sm font-bold tracking-widest uppercase opacity-80" data-id="element-555" data-mp="2index424z8ol">
          Serving all of Michigan from Fowlerville
        </p>
      </motion.div>
    </section>;
}